import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import SEO, { breadcrumbJsonLd } from '../../components/SEO';
import StartChooser from '../../components/StartChooser';
import { DEMO_URL, LITE_SIGNUP_URL } from '../../data/links';

/** Industries that usually deliver from one shop, kitchen or store: they get a Geofleet Lite band. */
const LITE_INDUSTRIES = new Set(['restaurants', 'pharmacies', 'grocery', 'flowers', 'laundry', 'water']);

export interface IndustryData {
  slug: string;
  icon: string;
  name: string;
  tagline: string;
  headline: string;
  headlineHighlight: string;
  description: string;
  heroImage: string;
  color: string;       // accent color hex e.g. '#FF6B35'
  colorLight: string;  // light bg tint e.g. '#FFF1EB'
  heroKeyPoints: string[];
  stats: { value: string; label: string }[];
  painPoints: { icon: string; title: string; description: string }[];
  features: { icon: string; title: string; description: string; tag?: string }[];
  testimonial: { quote: string; role: string; metric: string };
  useCases: string[];
}

export default function IndustryPage({ data }: { data: IndustryData }) {
  const { t } = useTranslation();
  const c = data.color;
  const cl = data.colorLight;
  const industryName = t(`industries.${data.slug}.name`);
  const heroKeyPoints = (t(`industries.${data.slug}.heroKeyPoints`, { returnObjects: true }) as string[]) || data.heroKeyPoints;
  const stats = (t(`industries.${data.slug}.stats`, { returnObjects: true }) as { value: string; label: string }[]) || data.stats;
  const painPoints = (t(`industries.${data.slug}.painPoints`, { returnObjects: true }) as { title: string; description: string }[]) || data.painPoints.map((p) => ({ title: p.title, description: p.description }));
  const features = (t(`industries.${data.slug}.features`, { returnObjects: true }) as { title: string; description: string; tag?: string }[]) || data.features.map((f) => ({ title: f.title, description: f.description, tag: f.tag }));
  const useCases = (t(`industries.${data.slug}.useCases`, { returnObjects: true }) as string[]) || data.useCases;

  return (
    <div className="pt-24">
      <SEO
        title={`${industryName} Delivery Management`}
        description={data.description}
        jsonLd={breadcrumbJsonLd([
          { name: 'Home', url: 'https://geofleet.ai' },
          { name: 'Industries', url: 'https://geofleet.ai/industries' },
          { name: industryName, url: `https://geofleet.ai/industries/${data.slug}` },
        ])}
      />
      {/* ─── Hero — unique per industry with gradient bg ─── */}
      <section
        className="relative py-20 lg:py-24 overflow-hidden"
        style={{ background: `linear-gradient(135deg, ${cl} 0%, #ffffff 60%)` }}
      >
        {/* Decorative circles */}
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full opacity-[0.07]" style={{ background: c }} />
        <div className="absolute -bottom-32 -left-16 w-72 h-72 rounded-full opacity-[0.05]" style={{ background: c }} />

        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              {/* Industry badge */}
              <div
                className="inline-flex items-center space-x-2 px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider mb-6"
                style={{ backgroundColor: cl, color: c }}
              >
                <i className={data.icon}></i>
                <span>{t(`industries.${data.slug}.tagline`)}</span>
              </div>

              <h1 className="text-3xl lg:text-[2.75rem] font-bold text-gray-900 leading-tight mb-5">
                {t(`industries.${data.slug}.headline`)}{' '}
                <span style={{ color: c }}>{t(`industries.${data.slug}.headlineHighlight`)}</span>
              </h1>

              <p className="text-base text-gray-600 leading-relaxed mb-6 max-w-lg">
                {t(`industries.${data.slug}.description`)}
              </p>

              {/* Key selling points */}
              <div className="space-y-2.5 mb-8">
                {heroKeyPoints.map((point, i) => (
                  <div key={i} className="flex items-center space-x-2.5">
                    <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: cl }}>
                      <i className="ri-check-line text-xs" style={{ color: c }}></i>
                    </div>
                    <span className="text-sm text-gray-700">{point}</span>
                  </div>
                ))}
              </div>

              <div className="flex flex-wrap gap-3 mb-8">
                <StartChooser
                  label={t('home.hero.ctaPrimary')}
                  className="bg-[#1769ff] text-white px-5 py-2.5 rounded-lg text-sm font-semibold hover:bg-[#0f55d4] transition-all flex items-center shadow-md cursor-pointer"
                />
                <a
                  href={DEMO_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white border border-gray-200 text-gray-700 px-5 py-2.5 rounded-lg text-sm font-medium hover:text-gray-900 transition-all flex items-center space-x-1.5 cursor-pointer"
                >
                  <i className="ri-calendar-line"></i>
                  <span>{t('common.ctaBookDemo')}</span>
                </a>
              </div>

              {/* Stats row */}
              <div className="flex flex-wrap gap-8">
                {stats.map((stat, i) => (
                  <div key={i}>
                    <div className="text-2xl font-bold" style={{ color: c }}>{stat.value}</div>
                    <div className="text-xs text-gray-500">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Hero visual */}
            <div className="relative">
              <div className="rounded-2xl overflow-hidden shadow-2xl border border-white/50">
                <img src={data.heroImage} alt={industryName} className="w-full h-auto object-cover" />
              </div>
              {/* Decorative accent border */}
              <div className="absolute -z-10 -inset-3 rounded-3xl opacity-20" style={{ background: `linear-gradient(135deg, ${c}, transparent)` }} />
            </div>
          </div>
        </div>
      </section>

      {LITE_INDUSTRIES.has(data.slug) ? (
        <section className="border-y border-blue-100 bg-[#f5f8ff]" aria-labelledby="industry-lite-title">
          <div className="mx-auto flex max-w-7xl flex-col gap-5 px-6 py-8 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-start gap-4">
              <span className="flex h-11 w-11 flex-none items-center justify-center rounded-xl bg-white text-xl text-[#1769ff] shadow-sm ring-1 ring-blue-100">
                <i className="ri-store-2-line" aria-hidden />
              </span>
              <div>
                <h2 id="industry-lite-title" className="text-lg font-bold text-slate-950">{t('industries.sections.liteTitle')}</h2>
                <p className="mt-1 max-w-2xl text-sm leading-relaxed text-slate-600">{t('industries.sections.liteText')}</p>
              </div>
            </div>
            <div className="flex flex-none flex-wrap gap-3">
              <a href={LITE_SIGNUP_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center rounded-lg bg-[#1769ff] px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#0f55d4]">
                {t('industries.sections.liteTrial')}
              </a>
              <Link to="/lite" className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-slate-800 transition-colors hover:bg-slate-50">
                {t('industries.sections.liteExplore')}
                <i className="ri-arrow-right-line rtl:rotate-180" aria-hidden />
              </Link>
            </div>
          </div>
        </section>
      ) : null}

      {/* ─── Pain Points ─── */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-3">
              {t('industries.sections.challengesTitle', { industry: industryName })}
            </h2>
            <p className="text-base text-gray-600 max-w-xl mx-auto">
              {t('industries.sections.challengesSub')}
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {data.painPoints.map((point, i) => (
              <div key={i} className="bg-[#FAFAFA] rounded-2xl p-6 border border-gray-100 hover:shadow-lg transition-all group">
                <div className="w-12 h-12 bg-red-50 rounded-xl flex items-center justify-center mb-4 group-hover:bg-red-100 transition-colors">
                  <i className={`${point.icon} text-red-400 text-xl`}></i>
                </div>
                <h3 className="text-base font-bold text-gray-900 mb-2">{painPoints[i]?.title ?? point.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{painPoints[i]?.description ?? point.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── How Geofleet Helps ─── */}
      <section className="py-16 bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <div
              className="inline-flex items-center space-x-2 px-3.5 py-1.5 rounded-full text-xs font-semibold mb-4"
              style={{ backgroundColor: cl, color: c }}
            >
              <i className="ri-sparkling-2-line"></i>
              <span>{t('industries.sections.aiPoweredSolution')}</span>
            </div>
            <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-3">
              {t('industries.sections.howGeofleetTransforms', { industry: industryName })}
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {data.features.map((feature, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 border border-gray-100 hover:shadow-lg transition-all group cursor-pointer relative">
                {features[i]?.tag && (
                  <span
                    className="absolute top-4 right-4 text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
                    style={{ backgroundColor: cl, color: c }}
                  >
                    {features[i].tag}
                  </span>
                )}
                <div
                  className="w-11 h-11 rounded-xl flex items-center justify-center mb-4 transition-colors"
                  style={{ backgroundColor: cl }}
                >
                  <i className={`${feature.icon} text-lg`} style={{ color: c }}></i>
                </div>
                <h3 className="text-sm font-bold text-gray-900 mb-1.5">{features[i]?.title ?? feature.title}</h3>
                <p className="text-xs text-gray-500 leading-relaxed">{features[i]?.description ?? feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Testimonial ─── */}
      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-6">
          <div className="rounded-2xl p-8 lg:p-10 shadow-lg text-center" style={{ background: `linear-gradient(135deg, ${cl}, #ffffff)` }}>
            <span
              className="inline-block text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full mb-5"
              style={{ backgroundColor: 'white', color: c }}
            >
              {t(`industries.testimonials.${data.slug}.metric`)}
            </span>
            <div className="flex justify-center mb-4">
              {[1, 2, 3, 4, 5].map(s => <i key={s} className="ri-star-fill text-[#FFB800]"></i>)}
            </div>
            <blockquote className="text-lg text-gray-800 font-medium leading-relaxed mb-6 max-w-xl mx-auto">
              "{t(`industries.testimonials.${data.slug}.quote`)}"
            </blockquote>
            <div className="text-sm font-bold text-gray-900">{t(`industries.testimonials.${data.slug}.role`)}</div>
            <div className="text-xs text-gray-500">{t('industries.anonymousAttribution')}</div>
          </div>
        </div>
      </section>

      {/* ─── Use Cases ─── */}
      <section className="py-16 bg-[#FAFAFA]">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-2xl font-bold text-gray-900 text-center mb-10">{t('industries.sections.perfectFor')}</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {useCases.map((uc, i) => (
              <div key={i} className="flex items-center space-x-3 bg-white rounded-xl px-5 py-4 border border-gray-100">
                <i className="ri-check-line flex-shrink-0" style={{ color: c }}></i>
                <span className="text-sm text-gray-700">{uc}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── CTA ─── */}
      <section className="py-16" style={{ backgroundColor: c }}>
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-2xl lg:text-3xl font-bold text-white mb-3">
            {t('industries.sections.readyToTransform', { industry: industryName })}
          </h2>
          <p className="text-base text-white/80 mb-7">
            {t('industries.sections.joinThousands', { industry: industryName })}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <StartChooser
              label={t('home.hero.ctaPrimary')}
              className="bg-white px-6 py-2.5 rounded-lg text-sm font-semibold hover:bg-gray-100 transition-all shadow-md cursor-pointer"
              style={{ color: c }}
            />
            <a href={DEMO_URL} target="_blank" rel="noopener noreferrer" className="border-2 border-white/80 text-white px-6 py-2.5 rounded-lg text-sm font-medium hover:bg-white/10 transition-all cursor-pointer">
              {t('industries.sections.talkToSales')}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
